import React, { useEffect, useRef, useState } from 'react';
import OptionsPanel, { PanelOptionProps } from './../../../shared/editor/generic/OptionsPanel';
import PanelOptions from './../meta/PanelOptions';
import { redrawProjectAction } from '../actions/chartEditor';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { RouteComponentProps } from 'react-router-dom';
import DataGrid from './../../../shared/wizard/data/DataGrid';
import ChartPreview from 'shared/wizard/data/ChartPreview';
import { cloneDeep } from 'lodash';
import TextSection from 'shared/wizard/editor/TextSection';
import { isUsingChartType } from 'shared/wizard/utils/seriesHelper';
import EditorPublish from './EditorPublish';
import WorkspaceBar from 'shared/editor/generic/WorkspaceBar';
import OptionsPanelContainer from 'shared/editor/generic/OptionsPanelContainer';
import ProjectContainer from 'shared/editor/generic/ProjectContainer';
import EditorError from './EditorError';
import { setAction } from 'redux/actions/projectConfig';

type ChartEditorProps = {
  showChartWizard: boolean;
  aggregatedOptions: any;
  history: RouteComponentProps['history'];
  chart: any;
  dataConfig: any;
  isMap: boolean;
};

const ChartEditor = (props: ChartEditorProps) => {
  const dispatch = useDispatch();
  const containerRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const { editorConfig } = useSelector((state: RootState) => state.chartEditorPage);
  const { tab, activeColumns, activeSection, isPastingActive } = useSelector((state: RootState) => state.projectConfig);
  const { resolution } = useSelector((state: RootState) => state.layoutEditorPage);
  const [panelOptions, setPanelOptions] = useState<Array<PanelOptionProps>>(PanelOptions);
  const inDataTab = tab === 'data';
  const isWordcloud = isUsingChartType(props.aggregatedOptions, 'wordcloud');
  const inAdvancedCustomizeMode = ['advanced', 'inspector'].includes(activeSection);

  const filterOptions = (options: Array<PanelOptionProps>) => {
    const editorOptions = editorConfig?.features;
    if (editorOptions) {
      const newOptions = options.filter((option) => {
        return !option.permission || (option.permission && editorOptions.indexOf(option.permission) > -1);
      });
      return newOptions;
    }
    return options;
  };

  useEffect(() => {
    if (!props.showChartWizard) dispatch(redrawProjectAction({}));
  }, [props.showChartWizard]);

  useEffect(() => {
    if (editorConfig) {
      const options = panelOptions.filter(
        (option) =>
          !option.permission ||
          !editorConfig.features ||
          (option.permission && editorConfig.features.includes(option.permission as string))
      );
      setPanelOptions(cloneDeep(options));
    }
  }, [editorConfig]);

  useEffect(() => {
    let options = cloneDeep(PanelOptions);

    if (props.isMap) {
      options = options.filter((option) => !option.mapDisabled);
    }
    setPanelOptions(filterOptions(options));
  }, [props.isMap]);

  const toggleIsPastingActive = (toggle?: boolean) => {
    dispatch(
      setAction({
        isPastingActive: toggle ?? !isPastingActive
      })
    );
  };

  return (
    <div className="story chart-type flex flex-wrap">
      <EditorPublish />
      <WorkspaceBar panelOptions={panelOptions} />

      <div className="flex w-full flex-col ev-sm:flex-row">
        <OptionsPanelContainer isAdvanced={inAdvancedCustomizeMode}>
          <OptionsPanel options={props.aggregatedOptions} panelOptions={panelOptions} />
        </OptionsPanelContainer>

        <ProjectContainer>
          <div
            className={'canvas-container ev-sm-max:h-[280px]'}
            style={resolution as React.CSSProperties}
            ref={containerRef}
          >
            {inDataTab && (
              <div className="relative everviz-datagrid h-full">
                {!isWordcloud && <DataGrid minRows={30} togglePasting={toggleIsPastingActive} />}
                {isWordcloud && <TextSection aggregatedOptions={props.aggregatedOptions} />}
              </div>
            )}
            <ChartPreview
              activeSeries={activeColumns.series}
              large={!inDataTab}
              redrawOnRender={true}
              parent={containerRef}
              inEditor={true}
            />
          </div>
        </ProjectContainer>
      </div>

      <EditorError />
    </div>
  );
};

export default ChartEditor;
